import {
    appPhotos,
    appMail as appMail_1,
    appOneDrive as appOneDrive_1,
    appOneNote as appOneNote_1,
    appSway as appSway_1,
    appSkype as appSkype_1,
    appOffice as appOffice_1,
    appBing as appBing_1,
    appMSN as appMSN_1,
    appFlow as appFlow_1,
    appForms as appForms_1,
    appBookings as appBookings_1,
} from './WafflePane.locstring.json';
import { appPowerPoint as appPowerPoint_1 } from 'owa-locstrings/lib/strings/apppowerpoint.locstring.json';
import { appExcel as appExcel_1 } from 'owa-locstrings/lib/strings/appexcel.locstring.json';
import { appWord as appWord_1 } from 'owa-locstrings/lib/strings/appword.locstring.json';
import { appTasks as appTasks_1 } from 'owa-locstrings/lib/strings/apptasks.locstring.json';
import { appPeople as appPeople_1 } from 'owa-locstrings/lib/strings/apppeople.locstring.json';
import { appCalendar as appCalendar_1 } from 'owa-locstrings/lib/strings/appcalendar.locstring.json';
import loc from 'owa-localize';
import HeaderPaneProps from './HeaderPaneProps';
import { computed } from 'mobx';
import { observer } from 'mobx-react-lite';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { Panel, PanelType } from 'office-ui-fabric-react/lib/Panel';
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getActiveCharm, HeaderCharmType } from 'owa-header-store';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';

import { getHostLocation } from 'owa-url/lib/hostLocation';
import * as React from 'react';
import {
    getMailPath,
    getCalendarPath,
    getPeoplePath,
    getFilesHubPath,
    getFilesHubImageTabPath,
    getOwaUrlWithAddedQueryParameters,
} from 'owa-url';

const styles = require('./WafflePane.scss');
const classNames = require('classnames');

const indexedReactDefaultPage = getMailPath() + 'default.aspx';
const indexedCalendarDefaultPage = getCalendarPath() + 'default.aspx';
const indexedPeopleDefaultPage = getPeoplePath() + 'default.aspx';

interface AppTile {
    displayName: string;
    icon: string;
    href: string;
    hoverBackground: string;
}

function createAppTile(
    displayName: string,
    icon: string,
    consumerHref: string,
    enterpriseHref: string,
    hoverBackground: string
): AppTile {
    return {
        displayName: displayName,
        icon: icon,
        href: isFeatureEnabled('fwk-consumerWaffle') ? consumerHref : enterpriseHref,
        hoverBackground: hoverBackground,
    };
}

function isOwaMailReactLocation() {
    const lowercasePathname = getHostLocation().pathname.toLowerCase();
    if (lowercasePathname === getMailPath() || lowercasePathname === indexedReactDefaultPage) {
        return true;
    }

    return false;
}

function isOwaCalendarReactLocation() {
    const lowercasePathname = getHostLocation().pathname.toLowerCase();
    if (
        lowercasePathname === getCalendarPath() ||
        lowercasePathname === indexedCalendarDefaultPage
    ) {
        return true;
    }

    return false;
}

function isOwaPeopleReactLocation() {
    const lowercasePathname = getHostLocation().pathname.toLowerCase();
    if (lowercasePathname === getPeoplePath() || lowercasePathname === indexedPeopleDefaultPage) {
        return true;
    }

    return false;
}

function isOwaFilesHubReactLocation() {
    const lowercasePathname = getHostLocation().pathname.toLowerCase();
    if (lowercasePathname === getFilesHubPath()) {
        return true;
    }

    return false;
}

function getMailAppHref() {
    return isOwaMailReactLocation() ? '' : getMailPath();
}

function getFilesHubAppImagesTabHref() {
    return isOwaFilesHubReactLocation() ? '' : getFilesHubImageTabPath();
}

function getCalendarAppHref() {
    return isOwaCalendarReactLocation() ? '' : getCalendarPath();
}

function getPeopleAppHref() {
    return isOwaPeopleReactLocation() ? '' : getPeoplePath();
}

function createPhotoTile(): AppTile {
    const appHRef = getFilesHubAppImagesTabHref();
    return createAppTile(loc(appPhotos), ControlIcons.Photo2, appHRef, appHRef, styles.appPhoto);
}

function isCalendarFlightSettingEnabled() {
    const userOptions = getUserConfiguration().UserOptions;
    if (userOptions && userOptions.ReactOptinSettings) {
        return userOptions.ReactOptinSettings.CalendarEnabled;
    } else {
        return false;
    }
}

function isPeopleFlightSettingEnabled() {
    const userOptions = getUserConfiguration().UserOptions;
    if (userOptions && userOptions.ReactOptinSettings) {
        return userOptions.ReactOptinSettings.PeopleEnabled;
    } else {
        return false;
    }
}

const Apps = computed(() =>
    [
        createAppTile(
            loc(appMail_1),
            ControlIcons.OutlookLogo,
            getMailAppHref(),
            getMailAppHref(),
            styles.appMail
        ),
        createAppTile(
            loc(appCalendar_1),
            ControlIcons.Calendar,
            isFeatureEnabled('cal-react') || isCalendarFlightSettingEnabled()
                ? getCalendarAppHref()
                : getOwaUrlWithAddedQueryParameters({ path: '/calendar' }),
            isFeatureEnabled('cal-react') || isCalendarFlightSettingEnabled()
                ? getCalendarAppHref()
                : getOwaUrlWithAddedQueryParameters({ path: '/calendar' }),
            styles.appCalendar
        ),
        createAppTile(
            loc(appPeople_1),
            ControlIcons.People,
            isFeatureEnabled('fwk-peopleReact') || isPeopleFlightSettingEnabled()
                ? getPeopleAppHref()
                : getOwaUrlWithAddedQueryParameters({ path: '/people' }),
            isFeatureEnabled('fwk-peopleReact') || isPeopleFlightSettingEnabled()
                ? getPeopleAppHref()
                : getOwaUrlWithAddedQueryParameters({ path: '/people' }),
            styles.appPeople
        ),
    ]
        .concat(isFeatureEnabled('doc-fileshub') && isConsumer() ? [createPhotoTile()] : [])
        .concat([
            createAppTile(
                loc(appOneDrive_1),
                ControlIcons.OneDriveLogo,
                'https://onedrive.live.com',
                'https://microsoft-my.sharepoint.com/_layouts/15/MySite.aspx?MySiteRedirect=AllDocuments',
                styles.appOneDrive
            ),
            createAppTile(
                loc(appTasks_1),
                ControlIcons.CheckboxComposite,
                getOwaUrlWithAddedQueryParameters({ path: '/tasks' }),
                getOwaUrlWithAddedQueryParameters({ path: '/tasks' }),
                styles.appTasks
            ),
            createAppTile(
                loc(appWord_1),
                ControlIcons.WordLogo,
                'https://office.live.com/start/Word.aspx?auth=1',
                'https://office.live.com/start/Word.aspx?auth=2',
                styles.appWord
            ),
            createAppTile(
                loc(appExcel_1),
                ControlIcons.ExcelLogo,
                'https://office.live.com/start/Excel.aspx?auth=1',
                'https://office.live.com/start/Excel.aspx?auth=2',
                styles.appExcel
            ),
            createAppTile(
                loc(appPowerPoint_1),
                ControlIcons.PowerPointLogo,
                'https://office.live.com/start/PowerPoint.aspx?auth=1',
                'https://office.live.com/start/PowerPoint.aspx?auth=2',
                styles.appPowerPoint
            ),
            createAppTile(
                loc(appOneNote_1),
                ControlIcons.OneNoteLogo,
                'https://www.onenote.com/notebooks?auth=1',
                'https://www.onenote.com/notebooks?auth=2',
                styles.appOneNote
            ),
            createAppTile(
                loc(appSway_1),
                ControlIcons.SwayLogoInverse,
                'https://sway.com',
                'https://sway.com',
                styles.appSway
            ),
        ])
        .concat(
            isFeatureEnabled('fwk-consumerWaffle')
                ? [
                      createAppTile(
                          loc(appSkype_1),
                          ControlIcons.SkypeLogo,
                          'https://web.skype.com/?source=owa',
                          '',
                          styles.appSkype
                      ),
                      createAppTile(
                          loc(appOffice_1),
                          ControlIcons.OfficeLogo,
                          'https://office.live.com/start/default.aspx?auth=1&nf=1',
                          '',
                          styles.appOffice
                      ),
                      createAppTile(
                          loc(appBing_1),
                          ControlIcons.BingLogo,
                          'https://www.bing.com/?FORM=MO0027',
                          '',
                          styles.appBing
                      ),
                      createAppTile(
                          loc(appMSN_1),
                          ControlIcons.MsnLogo,
                          'https://www.msn.com/?OCID=CALHeader',
                          '',
                          styles.appMSN
                      ),
                      createAppTile(
                          loc(appFlow_1),
                          ControlIcons.Flow,
                          'https://flow.microsoft.com/manage/?utm_source=consumer_office&utm_medium=app_launcher&utm_campaign=consumer_referrals',
                          '',
                          styles.appFlow
                      ),
                      createAppTile(
                          loc(appForms_1),
                          ControlIcons.OfficeFormsLogoInverse24,
                          `https://forms.office.com/Pages/DesignPage.aspx?auth_pvr=WindowsLiveId&auth_upn=${getUserConfiguration()
                              .SessionSettings.UserPrincipalName || ''}&origin=shell`,
                          '',
                          styles.appForms
                      ),
                  ]
                : []
        )
        .concat(
            isFeatureEnabled('fwk-bookingsModule')
                ? [
                      createAppTile(
                          loc(appBookings_1),
                          ControlIcons.BookingsLogo,
                          getOwaUrlWithAddedQueryParameters({ path: '/bookings' }),
                          getOwaUrlWithAddedQueryParameters({ path: '/bookings' }),
                          styles.appBookings
                      ),
                  ]
                : []
        )
);

interface AppsContextMenuProps {
    onDismiss: () => any;
}

const AppsContextMenu = observer((props: AppsContextMenuProps) => (
    <div className={styles.appsContainer}>
        {Apps.get().map(({ href, displayName, hoverBackground, icon }: AppTile) => {
            const tileClasses = classNames(styles.app, hoverBackground);

            const ariaProps: AriaProperties = {
                role: AriaRoles.menuitem,
                label: displayName,
            };

            return (
                <a
                    key={href}
                    data-is-focusable={true}
                    href={href}
                    target={'_blank'}
                    className={tileClasses}
                    onClick={props.onDismiss}
                    {...generateDomPropertiesForAria(ariaProps)}>
                    <Icon className={styles.icon} iconName={icon} />
                    <div className={styles.text}>{displayName}</div>
                </a>
            );
        })}
    </div>
));

const WaffleCharm = observer(function WaffleCharm(props: HeaderPaneProps) {
    return (
        <Panel
            className={styles.appPane}
            isLightDismiss={true}
            isOpen={getActiveCharm() === HeaderCharmType.WaffleCharm}
            hasCloseButton={false}
            onDismiss={props.onDismiss}
            type={PanelType.smallFixedNear}>
            <AppsContextMenu onDismiss={props.onDismiss} />
        </Panel>
    );
});
export default WaffleCharm;
